//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import logger from 'app/logger/logger';

const SUPPORTED_IMAGE_TYPES = {
  'image/bmp': 'bmp',
  'image/gif': 'gif',
  'image/jpeg': 'jpeg',
  'image/png': 'png',
  'image/webp': 'webp',
} as const;

const UNSUPPORTED_IMAGE_TYPES = {
  'image/jxr': 'jxr',
  'image/svg+xml': 'svg',
  'image/tiff': 'tiff',
  'image/vnd.wap.wbmp': 'wbmp',
  'image/x-jng': 'jng',
} as const;

const SUPPORTED_VIDEO_TYPES = {
  'video/3gpp': '3gpp',
  'video/mp4': 'mp4',
  'video/mpeg': 'mpeg',
  'video/ogg': 'ogv',
  'video/quicktime': 'mov',
  'video/webm': 'webm',
  'video/x-flv': 'flv',
  'video/x-mng': 'mng',
  'video/x-ms-asf': 'asf',
  'video/x-ms-wmv': 'wmv',
  'video/x-msvideo': 'avi',
  'video/x-matroska': 'mkv',
} as const;

const SUPPORTED_DOC_TYPES = {
  'application/msword': 'doc',
  'application/pdf': 'pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    'docx',
} as const;

export const XLSX_MIME_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const SUPPORTED_NON_MEDIA_TYPES = {
  'application/csv': 'csv',
  'text/csv': 'csv',
  [XLSX_MIME_TYPE]: 'xlsx',
} as const;

const MIME_TYPE_TO_EXTENSION: Record<string, string> = {
  ...SUPPORTED_NON_MEDIA_TYPES,
  ...UNSUPPORTED_IMAGE_TYPES,
  ...SUPPORTED_IMAGE_TYPES,
  ...SUPPORTED_VIDEO_TYPES,
  ...SUPPORTED_DOC_TYPES,
};

export function getExtensionForMimeType(mimeType: string): string | undefined {
  const extension = MIME_TYPE_TO_EXTENSION[mimeType.toLowerCase()];

  if (!extension) {
    logger.console.error(`Unable to find extension for mimeType ${mimeType}`);
  }

  return extension;
}

export function getExtensionForMimeTypeWithDotPrefix(
  mimeType: string,
): string | undefined {
  const extension = getExtensionForMimeType(mimeType);
  return extension ? `.${extension}` : extension;
}

/**
 * A helper to check if an image is supported using the mime type.
 * TIFF and HEIC files are currently not supported.
 */
export function isSupportedImageType(mimeType?: string) {
  const type = mimeType?.toLocaleLowerCase();

  return (
    type === 'image/jpeg' ||
    type === 'image/png' ||
    type === 'image/gif' ||
    type === 'image/bmp' ||
    type === 'image/webp'
  );
}

export type SupportedNonMediaMimeTypes = keyof typeof SUPPORTED_NON_MEDIA_TYPES;
export type SupportedImageMimeTypes = keyof typeof SUPPORTED_IMAGE_TYPES;
export type SupportedVideoMimeTypes = keyof typeof SUPPORTED_VIDEO_TYPES;
export type SupportedDocMimeTypes = keyof typeof SUPPORTED_DOC_TYPES;
export type SupportedMimeTypes =
  | SupportedDocMimeTypes
  | SupportedImageMimeTypes
  | SupportedNonMediaMimeTypes
  | SupportedVideoMimeTypes;

export const SUPPORTED_IMAGE_MIME_TYPES = Object.keys(
  SUPPORTED_IMAGE_TYPES,
) as SupportedImageMimeTypes[];

export const SUPPORTED_VIDEO_MIME_TYPES = Object.keys(
  SUPPORTED_VIDEO_TYPES,
) as SupportedVideoMimeTypes[];

export const SUPPORTED_DOC_MIME_TYPES = Object.keys(
  SUPPORTED_DOC_TYPES,
) as SupportedDocMimeTypes[];

export const SUPPORTED_NON_MEDIA_MIME_TYPES = Object.keys(
  SUPPORTED_NON_MEDIA_TYPES,
) as SupportedNonMediaMimeTypes[];

export const SUPPORTED_MIME_TYPES = [
  ...SUPPORTED_IMAGE_MIME_TYPES,
  ...SUPPORTED_VIDEO_MIME_TYPES,
] as SupportedMimeTypes[];

export const SUPPORTED_MIME_TYPES_FOR_CREATOR_EMAIL = [
  ...SUPPORTED_IMAGE_MIME_TYPES,
  ...SUPPORTED_VIDEO_MIME_TYPES,
  ...SUPPORTED_DOC_MIME_TYPES,
] as SupportedMimeTypes[];

export const SUPPORTED_MEDIA_EXTENSIONS_WITH_DOT_PREFIX = [
  ...SUPPORTED_IMAGE_MIME_TYPES.map(getExtensionForMimeTypeWithDotPrefix),
  ...SUPPORTED_VIDEO_MIME_TYPES.map(getExtensionForMimeTypeWithDotPrefix),
  '.jpg', //both .jpg and .jpeg comes under 'image/jpeg' mime type, adding it here since SUPPORTED_IMAGE_TYPES contains only .jpeg
] as string[];

export const CSV_HEADER = 'text/csv;charset=utf-8;';
